// Favorieten en (tijdelijk) verborgen activiteiten in localStorage (`gameon_prefs_v1`),
// met synchronisatie tussen tabbladen en automatisch verlopen van "een week verbergen".
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  HIDDEN_FOREVER,
  HIDE_WEEK_MS,
  hideActivity,
  isHidden,
  parsePreferences,
  prunePreferences,
  toggleFavorite as toggleFav,
  unhideActivity,
  type Preferences,
} from '../domain/prefs';
import { KEYS } from '../storage/keys';
import { getRaw, removeKey, setJSON } from '../storage/localStore';
import { useActivities } from './ActivitiesProvider';
import type { PrefsApi } from './contracts';

const PrefsContext = createContext<PrefsApi | null>(null);

function readPrefs(): Preferences {
  return parsePreferences(getRaw(KEYS.prefs));
}

/** Eerstvolgende verlooptijd van een tijdelijke verberging, of null. */
function nextExpiry(p: Preferences, now: number): number | null {
  let next: number | null = null;
  for (const until of Object.values(p.hiddenUntil)) {
    if (until > now && until < HIDDEN_FOREVER && (next === null || until < next)) next = until;
  }
  return next;
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const { activities, loading } = useActivities();
  const [prefs, setPrefs] = useState<Preferences>(readPrefs);
  const [now, setNow] = useState(() => Date.now());
  const ref = useRef(prefs);

  const write = useCallback((next: Preferences) => {
    ref.current = next;
    if (next.favorites.length === 0 && Object.keys(next.hiddenUntil).length === 0) removeKey(KEYS.prefs);
    else setJSON(KEYS.prefs, next);
    setPrefs(next);
  }, []);

  // Tabbladen synchroniseren
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== KEYS.prefs) return;
      const next = readPrefs();
      ref.current = next;
      setPrefs(next);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Verwijderde activiteiten en verlopen verbergingen opruimen
  useEffect(() => {
    if (loading || activities.length === 0) return;
    const ids = new Set(activities.map((a) => a.id));
    const pruned = prunePreferences(ref.current, Date.now(), ids);
    if (pruned !== ref.current) write(pruned);
  }, [activities, loading, now, write]);

  // Opnieuw beoordelen als een verberging verloopt, of als de app weer zichtbaar wordt
  useEffect(() => {
    const t = Date.now();
    const next = nextExpiry(prefs, t);
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (next !== null) {
      // setTimeout kan maximaal ~24,8 dagen; een week past ruim
      timer = setTimeout(() => setNow(Date.now()), Math.min(next - t + 50, 2_000_000_000));
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') setNow(Date.now());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (timer !== undefined) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [prefs]);

  const toggleFavorite = useCallback((id: number) => write(toggleFav(ref.current, id)), [write]);
  const hide = useCallback(
    (id: number, duration: 'week' | 'always') =>
      write(hideActivity(ref.current, id, duration === 'always' ? HIDDEN_FOREVER : Date.now() + HIDE_WEEK_MS)),
    [write],
  );
  const unhide = useCallback((id: number) => write(unhideActivity(ref.current, id)), [write]);
  const unhideAll = useCallback(() => write({ ...ref.current, hiddenUntil: {} }), [write]);

  const api = useMemo<PrefsApi>(() => {
    const favorites = new Set(prefs.favorites);
    const hiddenUntil: Record<string, number> = {};
    const hiddenIds = new Set<number>();
    for (const [k, until] of Object.entries(prefs.hiddenUntil)) {
      if (isHidden(prefs, Number(k), now)) {
        hiddenUntil[k] = until;
        hiddenIds.add(Number(k));
      }
    }
    return {
      favorites,
      hiddenIds,
      hiddenUntil,
      isFavorite: (id: number) => favorites.has(id),
      toggleFavorite,
      hide,
      unhide,
      unhideAll,
    };
  }, [prefs, now, toggleFavorite, hide, unhide, unhideAll]);

  return <PrefsContext.Provider value={api}>{children}</PrefsContext.Provider>;
}

export function usePrefs(): PrefsApi {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error('usePrefs moet binnen <PrefsProvider> gebruikt worden');
  return ctx;
}
